import Box from 'ext-ui/components/Box/Box'
import Pill from 'ext-ui/components/Pill/Pill'
import React from 'react'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import { useTranslate } from 'src/localization/Trns'
import ActivityFeed from 'src/page-components/Accounts/AccountActivityTab/components/ActivityFeed/ActivityFeed'
import { useAccountActivityData } from 'src/page-components/Accounts/AccountActivityTab/useAccountActivityData'
import {
  cardDetailTabSectionHeading,
  cardDetailTabSectionTitle,
} from 'src/page-components/Accounts/cardDetailTabs.style'

interface AccountActivityTabProps {
  /** The card whose activity to read. */
  creditCardId: string | undefined
}

/**
 * Activity tab content for the card-detail page (EX-40600).
 *
 * A chronological, newest-first audit feed of who changed the card and what
 * happened. Phase 1 is read-only by design — no filtering, export, dismissal,
 * or unread state; those are explicitly outside the MVP.
 *
 * Data arrives through `useAccountActivityData`, which reads the real
 * `GET /creditcards/{id}/cardmanagement/activity` route and falls back to the
 * demo feed for a card the endpoint has nothing for. `isDemoData` says which,
 * and drives the "Sample data" badge — read the warning on that hook before
 * enabling this tab anywhere a customer can reach it.
 *
 * Routing stays in `AccountEntityPage` — this is props-only tab content.
 */
const AccountActivityTab: React.FC<AccountActivityTabProps> = ({
  creditCardId,
}) => {
  const translate = useTranslate()
  const {
    events,
    isLoading,
    isLoadingMore,
    hasError,
    hasMore,
    loadMore,
    retry,
    isDemoData,
  } = useAccountActivityData(creditCardId)

  return (
    // Every sibling tab supplies its own BackgroundPageContent — that is where
    // the page gutters live, so without it this content renders flush to the
    // viewport edges.
    <BackgroundPageContent>
      {/*
        BackgroundPageContent renders only `className` — it does NOT spread the
        rest of its props — so the test id has to live on an element we own.
      */}
      <div data-testid="accountActivityTab-content">
        <Box>
          <div css={cardDetailTabSectionHeading}>
            <h2 css={cardDetailTabSectionTitle}>{translate('Activity')}</h2>
            {/*
              Shown only while the demo fallback is what the feed is rendering.
              Nothing else on this page distinguishes fabricated events from
              real ones, and an invented audit trail is more misleading than
              invented figures — it names people as having taken actions they
              never took. Delete this at the same time as the demo data, not
              before.
            */}
            {isDemoData && (
              <Pill color="pending" size="small">
                {translate('Sample data')}
              </Pill>
            )}
          </div>
          <ActivityFeed
            events={events}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasError={hasError}
            hasMore={hasMore}
            onLoadMore={loadMore}
            onRetry={retry}
          />
        </Box>
      </div>
    </BackgroundPageContent>
  )
}

export default AccountActivityTab
